import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (error) {
      // Endpoint always returns 200 — only show error on network failure
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/auth')}
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6 transition-colors"
          data-testid="back-to-auth-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </button>

        <div className="text-center mb-8">
          <img src="/logo.png" alt="FutureXV" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-heading font-bold">Forgot your password?</h1>
          <p className="text-zinc-400 text-sm mt-2">
            {submitted
              ? "Check your email for the reset link."
              : "Enter your email and we'll send you a link to reset it."}
          </p>
        </div>

        <Card className="bg-zinc-900 border-zinc-800 p-6">
          {submitted ? (
            <div className="text-center space-y-4" data-testid="forgot-password-confirmation">
              <div className="w-14 h-14 mx-auto bg-primary-neon/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-primary-neon" />
              </div>
              <p className="text-zinc-200">
                If an account exists for <span className="font-semibold">{email}</span>, a password reset link has been sent.
              </p>
              <p className="text-zinc-500 text-xs">
                The link expires in 30 minutes and can only be used once. Check your spam folder if you don't see it.
              </p>
              <Button
                onClick={() => navigate('/auth')}
                className="btn-primary w-full"
                data-testid="back-to-login-button"
              >
                Back to login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="forgot-email" className="text-zinc-300">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="forgot-email"
                    data-testid="forgot-email-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-zinc-950 border-zinc-800 text-white h-12"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="btn-primary w-full py-6"
                disabled={loading}
                data-testid="forgot-submit-button"
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
